<template>
  <main>
    <div class="row">
      <div class="col-12">
        <div class="tile">
          <div class="form-inline">
            <b-form-input class="mr-1 mb-1" v-model="filterFormModel.keyword" v-on:keydown.enter="load"
                          :placeholder="$t('keyword')"></b-form-input>
            <b-form-select class="mr-1 mb-1" v-model="filterFormModel.status" @change="load">
              <option :value="undefined">{{ $t('all') }}</option>
              <option value="0">{{ $t('partnerDomain.enabled') }}</option>
              <option value="1">{{ $t('partnerDomain.disabled') }}</option>
            </b-form-select>
            <button @click="load" class="btn btn-primary mr-1 mb-1">{{ $t('search') }}</button>
            <button @click="reset" class="btn btn-secondary mr-1 mb-1">Reset</button>
          </div>
        </div>
      </div>
      <div class="col-12">
        <div class="c-sorting" v-if="canWrite">
          <div class="row">
            <div class="col-lg-12 c-sorting__right">
              <div class="c-sorting__btn">
                <button @click="create" class="btn btn-sm btn-success">
                  <font-awesome-icon icon="plus"/>&nbsp;{{ $t('create') }}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div class="c-paging clearfix">
          <div class="c-paging__count" v-if="itemCount">{{ $t('found', [itemCount]) }}</div>
          <div class="c-paging__right">
            <div class="c-paging__numbers">
              <b-pagination v-if="itemCount > filterFormModel.pageSize" class="pull-right" size="sm"
                            v-model="filterFormModel.page" :total-rows="itemCount"
                            :per-page="filterFormModel.pageSize" @input="load"></b-pagination>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12" v-if="items.length">
        <div class="c-smart-table tile">
          <div class="row c-smart-table__title">
            <div class="col-lg-1"></div>
            <div class="col-lg-4">{{ $t('partnerDomain.domain') }}</div>
            <div class="col-lg-3">{{ $t('partnerDomain.colors') }}</div>
            <div class="col-lg-2">{{ $t('status') }}</div>
            <div class="col-lg-2"></div>
          </div>
          <div class="row" v-for="item in items" :key="item._id">
            <div class="col-lg-1">
              <img v-if="item.branding && item.branding.logo" :src="item.branding.logo" alt="logo"
                   style="height:32px;width:auto;max-width:72px;object-fit:contain;vertical-align:middle"/>
            </div>
            <div class="col-lg-4">
              <div><b>{{ item.name }}</b></div>
              <div><a :href="'https://' + item.domain" target="_blank">{{ item.domain }}</a></div>
              <div v-if="item.branding && item.branding.title" class="text-secondary">{{ item.branding.title }}</div>
            </div>
            <div class="col-lg-3">
              <template v-for="c in colorFields">
                <span v-if="item.branding && item.branding[c]" :key="c"
                      :title="c + ': ' + item.branding[c]"
                      :style="{ display:'inline-block', width:'18px', height:'18px', borderRadius:'3px', marginRight:'3px', border:'1px solid #ccc', background:item.branding[c] }"></span>
              </template>
            </div>
            <div class="col-lg-2">
              <span class="badge" :class="item.enabled ? 'badge-success' : 'badge-secondary'">
                {{ item.enabled ? $t('partnerDomain.enabled') : $t('partnerDomain.disabled') }}
              </span>
            </div>
            <div class="col-lg-2 text-right">
              <div class="c-buttons clearfix is-2" v-if="canWrite">
                <button @click="update(item._id)" type="button" class="btn btn-sm btn-info mr-1">
                  <font-awesome-icon icon="edit"/>&nbsp;{{ $t('edit') }}
                </button>
                <button @click="del(item._id)" type="button" v-if="canDelete"
                        class="btn btn-sm btn-danger mr-1">
                  <font-awesome-icon icon="trash"/>&nbsp;{{ $t('delete') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="col-12 text-center text-danger" v-if="!items.length && !listLoading">
        <span>{{ $t('notFoundSearch') }}</span>
      </div>
    </div>

    <b-modal v-model="showForm" size="lg" :title="formTitle" @ok="save" no-close-on-backdrop no-enforce-focus>
      <div class="row">
        <div class="col-md-12">
          <form-input :label="$t('partnerDomain.domainLabel')" :model="formModel" attribute="domain"
                      :errors="formErrors" placeholder="crm.doitac.com"></form-input>
          <form-input :label="$t('partnerDomain.name')" :model="formModel" attribute="name"
                      :errors="formErrors"></form-input>
          <form-input :label="$t('partnerDomain.titleField')" :model="formModel.branding" attribute="title"
                      :errors="formErrors"></form-input>

          <b-form-group :label="'Logo'">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.logo" :src="formModel.branding.logo" alt="logo"
                   style="max-height:40px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'logo')"></b-form-file>
              <button v-if="formModel.branding.logo" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.logo = ''">{{ $t('partnerDomain.clear') }}</button>
            </div>
          </b-form-group>

          <b-form-group :label="'Favicon'">
            <div class="d-flex align-items-center">
              <img v-if="formModel.branding.favicon" :src="formModel.branding.favicon" alt="favicon"
                   style="max-height:24px;margin-right:8px"/>
              <b-form-file accept="image/png,image/jpeg,image/jpg" :placeholder="$t('partnerDomain.choosePngJpg')"
                           @input="file => uploadAsset(file, 'favicon')"></b-form-file>
              <button v-if="formModel.branding.favicon" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding.favicon = ''">{{ $t('partnerDomain.clear') }}</button>
            </div>
          </b-form-group>

          <b-form-group v-for="c in colorFields" :key="c" :label="$t('partnerDomain.' + c)">
            <div class="d-flex align-items-center">
              <input type="color" :value="formModel.branding[c] || '#000000'"
                     @input="e => formModel.branding[c] = e.target.value" style="width:42px;height:32px;margin-right:8px"/>
              <b-form-input v-model="formModel.branding[c]" placeholder="#RRGGBB" style="max-width:140px"></b-form-input>
              <button v-if="formModel.branding[c]" type="button" class="btn btn-sm btn-link text-danger"
                      @click="formModel.branding[c] = ''">{{ $t('partnerDomain.clear') }}</button>
            </div>
          </b-form-group>

          <form-checkbox :chk-label="$t('partnerDomain.enabled')" :model="formModel" attribute="enabled"
                         :errors="formErrors"></form-checkbox>
        </div>
      </div>
    </b-modal>
  </main>
</template>

<script>
import List from '../../mixins/List';
import CRUD from "../../mixins/CRUD";

function emptyBranding() {
  return { logo: '', favicon: '', primaryColor: '', secondaryColor: '', sidebarBgColor: '', headerBgColor: '', title: '' };
}

export default {
  mixins: [List, CRUD],
  data: function () {
    return {
      service: '/partner-domain',
      autoLoad: true,
      colorFields: ['primaryColor', 'secondaryColor', 'sidebarBgColor', 'headerBgColor'],
      defaultFormModel: {
        domain: null,
        name: null,
        enabled: true,
        branding: emptyBranding()
      }
    }
  },
  computed: {
    canWrite() {
      return ['owner', 'admin'].includes(this.$store.state.user.role);
    },
    canDelete() {
      return ['owner'].includes(this.$store.state.user.role);
    }
  },
  i18n: {
    messages: {
      en: {
        all: 'All', status: 'Status',
        partnerDomain: {
          title: 'Partner domains', domain: 'Domain', domainLabel: 'Domain (FQDN)', name: 'Partner name',
          titleField: 'Display title', colors: 'Colors', enabled: 'Enabled', disabled: 'Disabled',
          clear: 'Clear', choosePngJpg: 'PNG/JPG only',
          primaryColor: 'Primary color', secondaryColor: 'Secondary color',
          sidebarBgColor: 'Sidebar background', headerBgColor: 'Header background'
        }
      },
      vi: {
        all: 'Tất cả', status: 'Trạng thái',
        partnerDomain: {
          title: 'Domain đối tác', domain: 'Domain', domainLabel: 'Domain (FQDN)', name: 'Tên đối tác',
          titleField: 'Tiêu đề hiển thị', colors: 'Màu sắc', enabled: 'Đang bật', disabled: 'Đang tắt',
          clear: 'Bỏ', choosePngJpg: 'Chỉ PNG/JPG',
          primaryColor: 'Màu chính', secondaryColor: 'Màu phụ',
          sidebarBgColor: 'Nền sidebar', headerBgColor: 'Nền header'
        }
      }
    }
  },
  methods: {
    load: function () {
      let loader = this.$loading.show();
      this.listLoading = true;
      this.$service.get(this.service, { params: this.filterFormModel }).then(response => {
        this.apply(response.data);
      }).catch(error => {
        if (error.response && error.response.data && error.response.data.message) {
          this.$swal({ text: error.response.data.message, icon: 'error' });
        }
      }).finally(() => {
        loader.hide();
        this.listLoading = false;
      });
    },
    create: function () {
      // Override để branding luôn là object MỚI (tránh share reference giữa các lần tạo)
      this.formModel = {
        domain: null,
        name: null,
        enabled: true,
        branding: emptyBranding()
      };
      this.formTitle = this.$t('create');
      this.formErrors = [];
      this.showForm = true;
    },
    update: function (id) {
      let loader = this.$loading.show();
      this.$service.get(this.service + '/' + id).then(response => {
        let data = response.data || {};
        // Đảm bảo branding tồn tại đủ field cho data cũ
        data.branding = Object.assign(emptyBranding(), data.branding || {});
        this.formModel = data;
        this.formModel.id = id;
        this.formTitle = this.$t('update');
        this.formErrors = [];
        this.showForm = true;
      }).finally(() => { loader.hide(); });
    },
    uploadAsset: function (file, field) {
      if (!file) return;
      const maxMb = 2;
      if (file.size / (1024 * 1024) > maxMb) {
        this.$swal({ text: this.$t('partnerDomain.choosePngJpg') + ' (≤ ' + maxMb + 'MB)', icon: 'error' });
        return;
      }
      let loader = this.$loading.show();
      const formData = new FormData();
      formData.append('file', file);
      this.$service.post('/upload/upload-image', formData).then(response => {
        const arr = response.data;
        if (Array.isArray(arr) && arr.length && arr[0].url) {
          this.$set(this.formModel.branding, field, arr[0].url);
        }
      }).catch(error => {
        const msg = error.response && error.response.data && error.response.data.message;
        this.$swal({ text: msg || 'Upload failed', icon: 'error' });
      }).finally(() => { loader.hide(); });
    }
  }
};
</script>
